import {
  createFileRoute,
  Outlet,
  useMatchRoute,
  useNavigate,
  useRouteContext,
} from "@tanstack/react-router";
import { getJwtPayload } from "@ncino/pa-authentication-client-webclient";
import { hasRemittanceAccess } from "@/services/auth/remittanceAccess";
import { LoadTransactionDataModal } from "./-internal/loadTransactionDataModal";
import { RemitWorkflowModal } from "./-internal/remitWorkflowModal";
import { PageHeader } from "@/shared/components/PageHeader";
import {
  useTranslation,
  Stack,
  Tabs,
  PaxTab,
  Divider,
  Typography,
  useClosableTabs,
  Dialog,
  Card,
  CardContent,
  PaxDataCardElement,
  registerPageTranslations,
} from "@ncino/pax-ui";
import { LoanPoolItem, ManageAnchorTabKeys } from "./-internal/dataTypes";
import { useState, useEffect } from "react";
import { getPoolOriginChip } from "./-internal/util";
import { ManageRouteContext } from "./-internal/ManageContext";
import { ChartLineUpIcon } from "@phosphor-icons/react";
import { RemittanceData } from "./-internal/ValidationStep/types";

export const Route = createFileRoute("/manage")({
  loader: async () => {
    const { default: en_US } = await import("./locales/en_US.json");
    registerPageTranslations("manage", { en_US });
  },
  component: RouteComponent,
  staticData: {
    breadcrumb: { title: ({ t }) => t("breadcrumbs.manage"), icon: ChartLineUpIcon },
  },
});

function RouteComponent() {
  const { t } = useTranslation("manage");
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();

  const { auth, institutionId } = useRouteContext({ from: "__root__" });
  const [canCreateRemittance, setCanCreateRemittance] = useState(false);

  const [isActionModalOpen, setIsActionModalOpen] = useState<boolean>(false);
  const [remitWorkflowModalIsOpen, setRemitWorkflowModalIsOpen] = useState(false);
  const [remittancePoolData, setRemittancePoolData] = useState<RemittanceData | null>(null);
  const [loadDataPoolItem, setLoadDataPoolItem] = useState<LoanPoolItem | null>(null);

  useEffect(() => {
    async function checkRemittanceAccess() {
      try {
        const token = await auth.tokenProvider();
        const principalId = getJwtPayload(token.token ?? "").sub;
        const allowed = await hasRemittanceAccess(
          principalId,
          institutionId,
          auth.tokenProvider,
          auth.authClient
        );
        setCanCreateRemittance(allowed);
      } catch (err) {
        if (import.meta.env.DEV) {
          console.debug("[remittanceAccess] Access check failed, defaulting to denied.", err);
        }
        setCanCreateRemittance(false);
      }
    }
    checkRemittanceAccess();
  }, []);

  const [openPoolItems, setOpenPoolItems] = useState<Record<string, LoanPoolItem>>({});
  const [anchorTab, setAnchorTab] = useState<ManageAnchorTabKeys>(() => {
    const isOnSold = matchRoute({ to: "/manage", search: { tab: "sold" }, includeSearch: true });
    return isOnSold ? "sold" : "purchased";
  });

  const {
    tabs: openPoolIds,
    active: selectedPoolId,
    setActive: setSelectedPoolId,
    addTab: addTabId,
    closeTab,
  } = useClosableTabs<string>({
    initialTabs: [],
    onTabClose: ({ wasFinalTab, closedTabId, newActiveTabId }) => {
      if (closedTabId) {
        const newItems = { ...openPoolItems };
        delete newItems[closedTabId];
        setOpenPoolItems(newItems);
      }

      if (wasFinalTab || !newActiveTabId) {
        navigate({
          to: "/manage",
          search: {
            tab: anchorTab,
          },
        });
      } else {
        navigate({
          to: "/manage/$poolId",
          params: {
            poolId: newActiveTabId,
          },
          search: {
            tab: "analytics",
            report: "overview",
          },
        });
      }
    },
  });

  const addPoolTab = (pool: LoanPoolItem, setAsActive?: boolean) => {
    setOpenPoolItems({
      ...openPoolItems,
      [pool.tabId]: pool,
    });
    addTabId(pool.tabId, { setAsActive });
  };
  return (
    <>
      <RemitWorkflowModal
        remitWorkflowModalIsOpen={remitWorkflowModalIsOpen}
        setRemitWorkflowModalIsOpen={setRemitWorkflowModalIsOpen}
        data={remittancePoolData ?? undefined}
      />
      <LoadTransactionDataModal
        open={loadDataPoolItem !== null}
        poolItem={loadDataPoolItem}
        onClose={() => setLoadDataPoolItem(null)}
      />
      <PageHeader
        title={t("managePoolsPage.title")}
        subheader={t("managePoolsPage.subtitle")}
        removeBottomPadding
        actionsBottom={
          <Stack
            direction="row"
            alignItems="center"
            divider={
              openPoolIds.length > 0 ? (
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              ) : (
                <></>
              )
            }
          >
            <Tabs
              value={selectedPoolId !== undefined ? false : anchorTab}
              aria-label={t("managePoolsPage.title") + " " + t("managePoolsPage.tabs")}
              onChange={(_, value) => {
                setSelectedPoolId(undefined);
                setAnchorTab(value);
                navigate({
                  to: "/manage",
                  search: {
                    tab: value,
                  },
                });
              }}
              sx={{ flexShrink: 0 }}
            >
              <PaxTab
                key={"tab-paxSoldPools"}
                value={"sold"}
                label={t("managePoolsPage.soldPoolsTab")}
                aria-label={t("managePoolsPage.soldPoolsTab")}
                fillOnSelect
              />
              <PaxTab
                key={"tab-paxPurchasedPools"}
                value={"purchased"}
                label={t("managePoolsPage.purchasedPoolsTab")}
                aria-label={t("managePoolsPage.purchasedPoolsTab")}
                fillOnSelect
              />
            </Tabs>

            <>
              {openPoolIds.length > 0 ? (
                <Typography
                  key={"tab-paxSelectedPoolLabel"}
                  aria-label={t("managePoolsPage.selectedPoolsLabel")}
                  variant="caption"
                  color="textSecondary"
                  sx={{ mx: 1, alignSelf: "center", whiteSpace: "nowrap" }}
                >
                  {t("managePoolsPage.selectedPoolsLabel")}:
                </Typography>
              ) : (
                <></>
              )}

              <Tabs
                value={selectedPoolId ?? false}
                aria-label={`${t("managePoolsPage.title")} ${t("managePoolsPage.selectedPoolsLabel")} ${t("managePoolsPage.tabs")}"`}
                variant="scrollable"
                onChange={(_, id) => {
                  setSelectedPoolId(id);
                  navigate({
                    to: "/manage/$poolId",
                    params: {
                      poolId: id,
                    },
                    search: {
                      tab: "analytics",
                      report: "overview",
                    },
                  });
                }}
              >
                {openPoolIds.map((id) => {
                  const poolItem = openPoolItems[id]!;
                  return (
                    <PaxTab
                      key={"tab-" + id}
                      value={id}
                      label={
                        <>
                          <Typography variant="inherit" component="span">
                            {poolItem.name}
                          </Typography>
                          {getPoolOriginChip(poolItem)}
                        </>
                      }
                      aria-label={poolItem.name}
                      closeIcon
                      onClose={() => closeTab(id)}
                      onMiddleClick={() => closeTab(id)}
                      fillOnSelect
                    />
                  );
                })}
              </Tabs>
            </>
          </Stack>
        }
      />

      <ManageRouteContext.Provider
        value={{
          openLoanPools: openPoolItems,
          addPoolTab,
          setAnchorTab,
          setRemitWorkflowModalIsOpen,
          setRemittancePoolData,
          setIsActionModalOpen,
          setLoadDataPoolItem,
          canCreateRemittance,
        }}
      >
        <Outlet />
      </ManageRouteContext.Provider>

      <Dialog open={isActionModalOpen} onClose={() => setIsActionModalOpen(false)}>
        <Card>
          <CardContent>
            <PaxDataCardElement title={"Placeholder"} value={"This is a placeholder element"} />
          </CardContent>
        </Card>
      </Dialog>
    </>
  );
}
